import { useToast } from "@chakra-ui/react";
import { useEffect } from "react";
import { useIntl } from "react-intl";

import { objectKeys } from "@nens/shared-utils";

import {
  FILE_NAME_KEY,
  FILE_REF_KEY,
  PATH_KEY,
  REVISION_ID_KEY,
} from "@/config/query-parameter-consts";
import { useRanaSearchState } from "@/hooks/router/rana-search-state";
import { useRanaTableData } from "@/hooks/router/use-rana-table-data";
import {
  useFileDirectoryQuery,
  useFileDirectoryQueryDirectly,
  useFileQuery,
  useFileQueryDirectly,
} from "@/queries/query-file";
import { useFileHistoryQuery } from "@/queries/query-file-history";
import { ProjectFile, ProjectFileOrDirectory } from "@/types/api-model-simple-names";
import { findDisabledFileHistoryReason } from "@/utils/disabled-file-history-props";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";
import { getSort } from "@/utils/get-sort";

const getPathFromPaths = (paths: string[]) => paths.join("/");

interface UseProjectFilesDataProps {
  initialPaths?: string[];
  enableSettingParams?: boolean;
}

// This is deprecated. Paths was a string array split by "," before. But we now want to support paths with "," in the name. So we now use a single string split by "/". But we keep this for backwards compatibility.
const DEPRECATED_PATHS_KEY = "paths";
const useFixDeprecatedPaths = () => {
  const [deprecatedPaths] = useRanaSearchState<string[]>(DEPRECATED_PATHS_KEY, [], ["string"], {
    disableSettingParams: false,
    dontEverSaveInHistory: true,
  });

  if (deprecatedPaths.length > 0) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete(DEPRECATED_PATHS_KEY);
    searchParams.set(PATH_KEY, getPathFromPaths(deprecatedPaths));
    window.history.replaceState(
      null,
      "",
      window.location.origin + window.location.pathname + "?" + searchParams.toString()
    );
  }
};

export function useProjectFilesData({
  initialPaths = [],
  enableSettingParams,
}: UseProjectFilesDataProps) {
  const toast = useToast();
  const intl = useIntl();
  const fetchFile = useFileQueryDirectly();

  const [fileRef, setFileRef] = useRanaSearchState<undefined | string>(
    FILE_REF_KEY,
    undefined,
    ["undefined", "string"],
    {
      disableSettingParams: !enableSettingParams,
      dontEverSaveInHistory: true,
    }
  );
  const [fileName, setFileName] = useRanaSearchState<undefined | string>(
    FILE_NAME_KEY,
    undefined,
    ["undefined", "string"],
    {
      disableSettingParams: !enableSettingParams,
      clearOthers: [REVISION_ID_KEY],
    }
  );

  useFixDeprecatedPaths();
  const [pathWithoutSlash, setPath] = useRanaSearchState<string>(
    PATH_KEY,
    getPathFromPaths(initialPaths),
    {
      disableSettingParams: !enableSettingParams,
    }
  );
  const path = pathWithoutSlash === "" ? pathWithoutSlash : pathWithoutSlash + "/";
  const paths = pathWithoutSlash ? pathWithoutSlash.split("/") : [];
  const setPaths = (newPaths: string[]) => {
    setPath(getPathFromPaths(newPaths));
  };

  const isDirectoryFileRef = !!fileRef && !fileName;
  const isFileFileRef = !!fileRef && !!fileName;

  const fileDirectoryQuery = useFileDirectoryQuery(path, isDirectoryFileRef ? fileRef : undefined);
  const fileDirectoryQueryDirectly = useFileDirectoryQueryDirectly();
  const { isLoading: isLoadingFiles, data, isError } = fileDirectoryQuery;

  const directoryVersionDoesNotExist = isError && isDirectoryFileRef;

  useEffect(() => {
    if (directoryVersionDoesNotExist) {
      setFileRef(undefined);
      toast({
        title: intl.formatMessage({
          id: "project-files.directory-version-not-found.title",
          defaultMessage: "Folder version does not exist",
        }),
        description: intl.formatMessage({
          id: "project-files.directory-version-not-found.description",
          defaultMessage: "We have cleared the folder version.",
        }),
        status: "error",
        duration: 10_000,
        isClosable: true,
      });
    }
  }, [directoryVersionDoesNotExist, intl, setFileRef, toast]);

  const { orderings, setOrdering, searchString, setSearchString } = useRanaTableData({
    keyAddon: "projectFiles",
  });
  const orderingKey = objectKeys(orderings)[0] as keyof ProjectFileOrDirectory;

  const filteredFiles = searchString
    ? (data?.items ?? []).filter(
        ({ id }) =>
          searchString && getNameFromFileId(id).toLowerCase().includes(searchString.toLowerCase())
      )
    : data?.items ?? [];

  const files = filteredFiles.slice().sort((a, b) => {
    // FUTURE: remove when descriptor is deprecated
    // Descriptor cannot be sorted
    if (orderingKey === "descriptor" || orderingKey === "meta") return 0;
    // Sort by type: directories should come before files
    if (a.type === "directory" && b.type !== "directory") return -1;
    if (a.type !== "directory" && b.type === "directory") return 1;

    // Secondary sorting based on the sortBy parameter
    if (orderingKey === "user") {
      return getSort(a.user?.givenName, b.user?.givenName, orderings[orderingKey]);
    }

    return getSort(a[orderingKey], b[orderingKey], orderings[orderingKey]);
  });

  const fileNoRef = files.find(
    (file) => getNameFromFileId(file.id) === fileName && file.type === "file"
  );

  // If there is no fileRef defined and we cannot find the file with the given fileName,
  // It has probably been deleted. So we try to find the last valid fileRef from the file history and set it.
  // If there are no history items, the file probably never existed, so we clear the fileName.
  // Only do this when enableSettingParams is true, otherwise the fileName can also be set by an external input (like the set file name in define study area)
  // Also show toasts for both cases to inform the user about what happened.
  const { data: unknownFileHistoryQuery, isLoading: isLoadingHistory } = useFileHistoryQuery(
    enableSettingParams && !isLoadingFiles && !fileRef && !fileNoRef && fileName
      ? path + fileName
      : undefined
  );
  // This is the ref of the last valid file version. So a version where the file was not deleted or moved away.
  const lastValidFileRef = unknownFileHistoryQuery?.items.find(
    (historyItem) => !findDisabledFileHistoryReason(historyItem.message)
  )?.ref;
  const fileNeverExisted = unknownFileHistoryQuery?.items.length === 0;

  useEffect(() => {
    const resolveDeletedFile = async () => {
      // If the file never existed, we clear the fileName and show a toast to inform the user.
      if (fileNeverExisted) {
        setFileName(undefined);
        toast({
          title: intl.formatMessage({
            id: "project-files.file-never-existed.title",
            defaultMessage: "File does not exist (and has never existed)",
          }),
          description: intl.formatMessage({
            id: "project-files.file-never-existed.description",
            defaultMessage: "We have cleared the file selection.",
          }),
          status: "error",
          duration: 10_000,
          isClosable: true,
        });

        return;
      }

      if (!lastValidFileRef) {
        return;
      }

      const { data: fileWithRef } = await fetchFile(path + fileName, lastValidFileRef);

      // If the deleted file is a shapefile, we need to inform the user that it has been converted to a GeoPackage and set the fileName accordingly.
      if (fileWithRef?.dataType === "shapefile") {
        // Convert "test.shp" to "test.gpkg" because that is the file extension of the converted shapefile.
        setFileName(getNameFromFileId(fileWithRef.id).split(".")[0] + ".gpkg");

        toast({
          title: intl.formatMessage({
            id: "project-files.file-converted-to-gpkg.title",
            defaultMessage: "Shapefile has been converted to a GeoPackage",
          }),
          description: intl.formatMessage({
            id: "project-files.file-converted-to-gpkg.description",
            defaultMessage: "We have set the file extension to .gpkg",
          }),
          status: "info",
          duration: 10_000,
          isClosable: true,
        });

        return;
      }

      // If the file is not a shapefile, we just set the fileRef to the last valid fileRef and inform the user.
      setFileRef(lastValidFileRef);
      toast({
        title: intl.formatMessage({
          id: "project-files.file-ref-restored.title",
          defaultMessage: "File has been deleted, but a valid version has been found",
        }),
        description: intl.formatMessage({
          id: "project-files.file-ref-restored.description",
          defaultMessage: "We have set the file version to the last valid version.",
        }),
        status: "info",
        duration: 10_000,
        isClosable: true,
      });
    };
    resolveDeletedFile();
  }, [
    fetchFile,
    fileName,
    fileNeverExisted,
    intl,
    lastValidFileRef,
    path,
    setFileName,
    setFileRef,
    toast,
  ]);

  const {
    data: fileWithRef,
    isError: isFileRefError,
    isLoading: isLoadingFile,
  } = useFileQuery(
    isFileFileRef ? path + fileName : undefined,
    isFileFileRef ? fileRef : undefined
  );

  // If fileRef is the latest version remove it.
  if (
    isFileFileRef &&
    fileWithRef &&
    fileNoRef &&
    fileWithRef.lastModified.toString() === fileNoRef.lastModified.toString()
  ) {
    setFileRef(undefined);
  }

  useEffect(() => {
    if (isFileRefError) {
      setFileRef(undefined);
      toast({
        title: intl.formatMessage({
          id: "project-files.file-version-not-found.title",
          defaultMessage: "File version does not exist",
        }),
        description: intl.formatMessage({
          id: "project-files.file-version-not-found.description",
          defaultMessage: "We have cleared the file version.",
        }),
        status: "error",
        duration: 10_000,
        isClosable: true,
      });
    }
  }, [fileNeverExisted, intl, isFileRefError, lastValidFileRef, setFileName, setFileRef, toast]);

  const file = (isFileFileRef ? fileWithRef || undefined : fileNoRef) as ProjectFile | undefined;

  const tableDataIds = files.map((item) => item.id) ?? [];

  const onDirectoryOrFileClick = (fileOrDirectory: ProjectFileOrDirectory) => {
    const name = getNameFromFileId(fileOrDirectory.id);
    fileOrDirectory.type === "file" ? setFileName(name) : setPaths([...paths, name]);
  };

  return {
    file,
    setFileName,
    clearFile: () => {
      setFileName(undefined);
      setFileRef(undefined);
    },
    fileName,
    fileRef,
    paths,
    setPaths,
    path,
    intl,
    files,
    isLoading: isLoadingFiles || isLoadingFile || isLoadingHistory,
    tableDataIds,
    onDirectoryOrFileClick,
    orderings,
    setOrdering,
    searchString,
    setSearchString,
    // Exposed for use-project-files-dropzone
    fileDirectoryQuery,
    fileDirectoryQueryDirectly,
  };
}
